import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Lock } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton, Typography } from '@mui/material';
import PostAddIcon from '@mui/icons-material/PostAdd';
import styles from './navigationComponent.module.css';
import UI from '../../constants/ui';
import URL from '../../constants/urls';
import { API_URL, IMAGE_URL } from '../../constants/apiUrls';
import AvailablePoints from '../availablePoints';
import NotificationIcon from '../notifications/NotificationIcon';
import ICON_OTHER_ROUTES from '../../img/otherRoutes.svg';
import NewTag from '../../components/newTag/NewTag';
import RenderNavBarButtons from './RenderNavBarButtons';
import LOGIN_TYPE from '../../constants/loginType';
import RenderMenu from './RenderMenu';
import useWindowSize from '../../hooks/useWindowSize';
import { getLoginDetailFromSession } from '../../helpers/sessionDetails';
import { EMPLOYER_LOGIN_ESTATE } from '../../constants/loginStates.constants';
import STORAGE_KEY from '../../constants/storageKey';
import {
  setAvailablePointsZero,
  setLoggedInUserRole,
  setRelevantToCareerPath,
  setRequestsInComplete,
  setRerenderpermissions,
  setUserRole,
} from '../../../redux/userSlice';
import { postRequest } from '../../services';
import STATUS_CODE from '../../constants/statusCode';
import GA from '../../constants/analytics.constants';
import useGAEventsTracker from '../../hooks/useGAEventsTracker';
import ServicesMenu from './ServiceLink';

function RenderWebView(props) {
  const [shouldOpen, setShouldOpen] = useState(false);
  const { windowWidth } = useWindowSize();

  const notificationCount = useSelector(
    (state) => state.user.userInfo.notificationsCount
  );

  const reRenderPermissionsCode = useSelector(
    (state) => state.user.reRenderPermissionsCode
  );

  const isApprovalPending = useMemo(
    () =>
      getLoginDetailFromSession()?.estate ===
      EMPLOYER_LOGIN_ESTATE.APPROVAL_PENDING,
    [reRenderPermissionsCode]
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    loggedInUserRole,
    currentHomeUrl,
    activeUrl,
    hasUpdateJobPermission,
    project,
    isMobileDevice,
  } = props;

  const GALogoutEventsTracker = useGAEventsTracker(GA.EVENT_BUTTON.LOGOUT);

  const handleLogout = () => {
    const role = getLoginDetailFromSession()?.role;
    dispatch(setUserRole(role));
    postRequest(API_URL.LOGOUT)
      .then((res) => {
        if (res.code === STATUS_CODE.SUCCESSFULLY_LOGOUT) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          GALogoutEventsTracker(getLoginDetailFromSession()?.name + GA.LOGOUT);
          localStorage.removeItem(STORAGE_KEY.CANDIDATE_DETAILS);
          localStorage.removeItem(STORAGE_KEY.EMPLOYER_DETAILS);
          localStorage.removeItem(STORAGE_KEY.SESSION_DETAILS);
          localStorage.removeItem(STORAGE_KEY.PRICE_PLAN_DETAILS);
          setLoggedInUserRole();
          if (role === LOGIN_TYPE.CANDIDATE) {
            window.location.reload();
            navigate(URL.HOME);
          } else if (role === LOGIN_TYPE.EMPLOYER) {
            window.location.reload();
            navigate(URL.RECRUITERS);
          }

          dispatch(setRequestsInComplete());
          dispatch(setRerenderpermissions());
          dispatch(setAvailablePointsZero());
          setShouldOpen(!shouldOpen);
          window.location.reload(true);
        }
      })
      .catch(() => {});
  };

  const onSelectNavOption = (url) => {
    setShouldOpen(!shouldOpen);
    navigate(url);
    if (getLoginDetailFromSession()?.careerpath) {
      dispatch(setRelevantToCareerPath(true));
    } else {
      dispatch(setRelevantToCareerPath(false));
    }
  };

  return (
    <>
      <div>
        <Link
          to={currentHomeUrl}
          className={`${styles.logo} color-1D8FF2 col-2`}
        >
          <img
            src={IMAGE_URL.ALIGNMYCAREER}
            alt={UI.ALT_ALIGNMYCAREER}
            className={`${isMobileDevice ? 'logo_mobile' : 'website_logo'}`}
            height="100%"
          />
        </Link>
      </div>
      <div className={` my-3 d-flex py-1 justify-content-center`}>
        {loggedInUserRole ? (
          <RenderNavBarButtons
            loggedInUserRole={loggedInUserRole}
            activeUrl={activeUrl}
          />
        ) : (
          <>
            <div className="me-3">
              <Link
                to={URL.FIND_JOBS}
                className={`color-1F2830 ${styles.navLink}  ${
                  activeUrl === URL.FIND_JOBS ? `subtitle-1-bold` : 'subtitle-1'
                }`}
              >
                {UI.FIND_JOBS}
              </Link>
              {activeUrl === URL.FIND_JOBS ? (
                <div className={`background-1D8FF2 ${styles.active}`} />
              ) : (
                ''
              )}
            </div>
            <div className="ms-3">
              <Link
                to={URL.FIND_CANDIDATES}
                className={`color-1F2830 ${styles.navLink} ${
                  activeUrl === URL.FIND_CANDIDATES
                    ? `subtitle-1-bold ${styles.active}`
                    : 'subtitle-1'
                }`}
              >
                {UI.FIND_TALENT}
              </Link>
              {activeUrl === URL.FIND_CANDIDATES ? (
                <div className={`background-1D8FF2 ${styles.active}`} />
              ) : (
                ''
              )}
            </div>
            <div>
              <ServicesMenu />
            </div>
          </>
        )}
      </div>

      <div className="d-flex justify-content-end align-items-center">
        {window.location.pathname !== URL.BLOG_PAGE && (
          <div
            className="nav-item"
            style={{ width: '7rem', position: 'relative' }}
          >
            <Link
              to={URL.BLOG_PAGE}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'black' }}
            >
              {UI.BLOG_PAGE}
            </Link>
            <span
              style={{
                marginLeft: '4px',
                position: 'absolute',
                top: '-5px',
              }}
            >
              <NewTag paddingValue={3} />
            </span>
          </div>
        )}
        {loggedInUserRole &&
          (loggedInUserRole === LOGIN_TYPE.CANDIDATE ||
            loggedInUserRole === LOGIN_TYPE.EMPLOYER) && (
            <div className="d-flex">
              {loggedInUserRole === LOGIN_TYPE.CANDIDATE && <AvailablePoints />}
              <NotificationIcon />
            </div>
          )}
        {!!loggedInUserRole &&
          loggedInUserRole === LOGIN_TYPE.EMPLOYER &&
          !isMobileDevice && (
            <IconButton
              onClick={() => {
                navigate(URL.POST_JOB);
              }}
              disabled={!hasUpdateJobPermission}
            >
              <div
                className={`${
                  hasUpdateJobPermission
                    ? 'background-1D8FF2'
                    : 'background-D7D7D7'
                } ${styles.groupedRoutes} px-2`}
              >
                {hasUpdateJobPermission ? (
                  <PostAddIcon sx={{ color: 'white' }} />
                ) : (
                  <Lock sx={{ color: 'white' }} />
                )}

                <Typography
                  component="span"
                  sx={{ fontSize: '1rem', color: 'white', margin: '0.5rem' }}
                >
                  {UI.POST_JOB}
                </Typography>
              </div>
            </IconButton>
          )}
        <IconButton onClick={() => setShouldOpen(true)}>
          <div className={`background-1D8FF2 ${styles.groupedRoutes} px-2`}>
            {loggedInUserRole ? (
              <img
                width="72px"
                height="32px"
                src={ICON_OTHER_ROUTES}
                alt={UI.ALT_OTHER_PAGES}
              />
            ) : (
              <Typography
                component="span"
                sx={{ fontSize: '1rem', color: 'white', margin: '0.5rem' }}
              >
                {UI.LOGIN_REGISTER}
              </Typography>
            )}
          </div>
        </IconButton>
        <RenderMenu
          shouldOpen={shouldOpen}
          setShouldOpen={setShouldOpen}
          isMobileDevice={isMobileDevice}
          activeUrl={activeUrl}
          loggedInUserRole={loggedInUserRole}
          handleLogout={handleLogout}
          onSelectNavOption={onSelectNavOption}
          hasUpdateJobPermission={hasUpdateJobPermission}
          project={project}
          isApprovalPending={isApprovalPending}
          notificationCount={notificationCount}
          windowWidth={windowWidth}
        />
      </div>
    </>
  );
}

RenderWebView.propTypes = {
  loggedInUserRole: PropTypes.number,
  currentHomeUrl: PropTypes.string.isRequired,
  activeUrl: PropTypes.string.isRequired,
  hasUpdateJobPermission: PropTypes.bool.isRequired,
  project: PropTypes.string,
  isMobileDevice: PropTypes.bool.isRequired,
};

RenderWebView.defaultProps = {
  loggedInUserRole: undefined,
  project: '',
};

export default RenderWebView;
